import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { AlertBannerContext } from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import {
  authorizeQuery,
  loginMutation,
  logoutMutation,
  createSecondarySuperAdminMutation,
} from "../../Graphql/querries/authQuery";
import type { AuthorizeResponse } from "../../Graphql/types/auth";
import { isSuperAdminRole } from "../../Graphql/types/auth";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { API_RESPONSE_CODE } from "@kivunova/kivufrontendcommon";

/** Call authorize (uses cookies). Returns parsed response. */
export function useAuthorizeAdmin() {
  const { setAlert } = useContext(AlertBannerContext);
  const { setAuthenticated, setAccessDenied, setUnauthenticated } =
    useAdminAuth();

  return useMutation({
    mutationKey: ["authorizeAdmin"],
    mutationFn: async (): Promise<AuthorizeResponse> => {
      return fetchGraphQL(authorizeQuery, { input: {} }, setAlert);
    },
    onSuccess: (response) => {
      const { authorize: data } = response;
      if (data.code !== API_RESPONSE_CODE.OK || !data.authorized) {
        setUnauthenticated();
        return;
      }
      if (isSuperAdminRole(data.role)) {
        setAuthenticated({
          userId: data.userId ?? "",
          role: data.role ?? "",
          companyId: data.companyId ?? null,
          storeId: data.storeId ?? null,
        });
      } else {
        setAccessDenied();
      }
    },
    onError: () => {
      setUnauthenticated();
    },
  });
}

/** Login then authorize; establishes admin session only if user has super-admin role. */
export function useLoginAdmin() {
  const { setAlert } = useContext(AlertBannerContext);
  const { setAuthenticated, setAccessDenied, setUnauthenticated } =
    useAdminAuth();

  return useMutation({
    mutationKey: ["loginAdmin"],
    mutationFn: async (input: { email: string; password: string }) => {
      await fetchGraphQL(loginMutation, { input }, setAlert);
      const res = await fetchGraphQL<AuthorizeResponse>(
        authorizeQuery,
        { input: {} },
        setAlert,
      );
      const data = res.authorize;
      if (
        data.code === API_RESPONSE_CODE.OK &&
        data.authorized &&
        isSuperAdminRole(data.role)
      ) {
        setAuthenticated({
          userId: data.userId ?? "",
          role: data.role ?? "",
          companyId: data.companyId ?? null,
          storeId: data.storeId ?? null,
        });
      } else if (data.authorized) {
        setAccessDenied();
        throw new Error("ACCESS_DENIED");
      } else {
        setUnauthenticated();
        throw new Error(data.code ?? "UNAUTHORIZED");
      }
    },
  });
}

/** Logout (clears cookies via Proxy) and local state. */
export function useLogoutAdmin() {
  const { setAlert } = useContext(AlertBannerContext);
  const { setUnauthenticated } = useAdminAuth();

  return useMutation({
    mutationKey: ["logoutAdmin"],
    mutationFn: async () => {
      await fetchGraphQL(logoutMutation, { input: {} }, setAlert);
    },
    onSettled: () => {
      setUnauthenticated();
    },
  });
}

export type CreateSecondarySuperAdminInput = {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
};

export type CreateSecondarySuperAdminResponse = {
  createSecondarySuperAdmin: {
    code: string;
    user?: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      role: string;
    } | null;
  };
};

/** Create a Secondary Super Admin user (Proxy → Identity POST /users/superadmin/create). */
export function useCreateSecondarySuperAdmin() {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation({
    mutationKey: ["createSecondarySuperAdmin"],
    mutationFn: async (
      input: CreateSecondarySuperAdminInput,
    ): Promise<CreateSecondarySuperAdminResponse> => {
      return fetchGraphQL(
        createSecondarySuperAdminMutation,
        { input },
        setAlert,
      );
    },
  });
}
